import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Banner from '../Components/Banner';
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import ServiceNowLeadForm from "../Components/ServiceNowLeadForm";

const ServiceNowLinks = () => {
    const serviceNowPages = [
        {
            title: "ServiceNow Staff Augmentation",
            description:
                "Extend your ServiceNow team with experienced developers, consultants, architects, administrators, integration specialists, and QA professionals.",
            path: "/servicenow-staff-augmentation",
        },
        {
            title: "Hire ServiceNow Developers",
            description:
                "Hire experienced ServiceNow developers for custom development, integrations, workflow automation, platform customization, and ongoing projects.",
            path: "/hire-servicenow-developers",
        },
        {
            title: "ServiceNow Implementation Partner",
            description:
                "Implement ServiceNow with support for planning, configuration, customization, integrations, workflow automation, testing, and deployment.",
            path: "/servicenow-implementation-partner",
        },
        {
            title: "ServiceNow Managed Services",
            description:
                "Get ongoing ServiceNow support, maintenance, administration, enhancements, integrations, upgrades, and platform management.",
            path: "/servicenow-managed-services",
        },
        {
            title: "ServiceNow Consulting Company",
            description:
                "Get expert ServiceNow consulting for strategy, platform assessment, optimization, ITSM, ITOM, integrations, automation, and platform planning.",
            path: "/servicenow-consulting-company",
        },
        {
            title: "Dedicated ServiceNow Team",
            description:
                "Build a dedicated ServiceNow team with developers, consultants, architects, administrators, integration specialists, and QA professionals.",
            path: "/dedicated-servicenow-team",
        },
    ];

    return (
        <>
        <Helmet>

    {/* =========================
        PRIMARY SEO
    ========================== */}

    <title>
        ServiceNow Services & Solutions | Consulting, Implementation & Support | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode provides ServiceNow services including consulting, implementation, managed services, staff augmentation, hiring ServiceNow developers, dedicated ServiceNow teams, integration, ITSM, ITOM, CSM, HRSD, and custom ServiceNow development."
    />

    <meta
        name="keywords"
        content="
        ServiceNow services,
        ServiceNow solutions,
        ServiceNow consulting services,
        ServiceNow consulting company,
        ServiceNow consulting firm,
        ServiceNow consultants,
        ServiceNow consulting partner,
        ServiceNow implementation services,
        ServiceNow implementation partner,
        ServiceNow implementation company,
        ServiceNow implementation consultant,
        ServiceNow implementation cost,
        ServiceNow implementation timeline,
        ServiceNow managed services,
        ServiceNow managed support,
        ServiceNow support services,
        ServiceNow maintenance services,
        ServiceNow platform support,
        ServiceNow administration services,
        ServiceNow ongoing support,
        ServiceNow upgrade services,
        ServiceNow staff augmentation,
        ServiceNow resource augmentation,
        ServiceNow team augmentation,
        ServiceNow staffing,
        ServiceNow staffing agency,
        ServiceNow augmentation partner,
        ServiceNow remote developers,
        ServiceNow offshore developers,
        hire ServiceNow developers,
        ServiceNow contract developers,
        ServiceNow developers,
        ServiceNow developers India,
        ServiceNow development services,
        ServiceNow developer hourly rate,
        certified ServiceNow developers,
        dedicated ServiceNow team,
        dedicated ServiceNow developers,
        dedicated ServiceNow development team,
        ServiceNow development team,
        ServiceNow remote team,
        ServiceNow offshore team,
        ServiceNow integration services,
        ServiceNow integration consulting,
        ServiceNow integrations,
        ServiceNow REST API integration,
        ServiceNow SOAP integration,
        ServiceNow IntegrationHub,
        ServiceNow ITSM,
        ServiceNow ITSM implementation,
        ServiceNow ITSM consulting,
        ServiceNow ITSM support,
        ServiceNow ITOM,
        ServiceNow ITOM implementation,
        ServiceNow ITOM consulting,
        ServiceNow ITOM support,
        ServiceNow CMDB,
        ServiceNow CMDB implementation,
        ServiceNow CMDB consulting,
        ServiceNow CSM,
        ServiceNow CSM implementation,
        ServiceNow CSM consulting,
        ServiceNow CSM support,
        ServiceNow HRSD,
        ServiceNow HRSD implementation,
        ServiceNow HRSD consulting,
        ServiceNow HRSD support,
        ServiceNow workflow automation,
        ServiceNow custom development,
        ServiceNow custom application development,
        ServiceNow platform customization,
        ServiceNow automation,
        ServiceNow digital transformation,
        ServiceNow partner,
        ServiceNow technology partner
        "
    />

    {/* =========================
        CANONICAL
    ========================== */}

    <link
        rel="canonical"
        href="https://www.mobikode.com/servicenow-links"
    />


    {/* =========================
        OPEN GRAPH
    ========================== */}

    <meta
        property="og:title"
        content="ServiceNow Services & Solutions | Mobikode"
    />

    <meta
        property="og:description"
        content="Explore Mobikode's ServiceNow services including consulting, implementation, managed services, staff augmentation, ServiceNow developers, dedicated teams, integrations, ITSM, ITOM, CSM, HRSD, and custom development."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/servicenow-links"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:site_name"
        content="Mobikode"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/services/servicenow.jpg"
    />

    <meta
        property="og:image:alt"
        content="ServiceNow Services, Consulting, Implementation and Support by Mobikode"
    />


    {/* =========================
        TWITTER / X
    ========================== */}

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="ServiceNow Services & Solutions | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Mobikode provides ServiceNow consulting, implementation, managed services, staff augmentation, developers, dedicated teams, integrations, ITSM, ITOM, CSM, HRSD, and custom ServiceNow solutions."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/services/servicenow.jpg"
    />

    <meta
        name="twitter:image:alt"
        content="ServiceNow Services and Solutions by Mobikode"
    />

</Helmet>
<section className="relative h-[500px] lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">
    <div className="absolute inset-0 z-0">
        <img
            src="/assets/services/bg3.png"
            alt="ServiceNow services and solutions for enterprise organizations"
            className="h-full w-full object-cover opacity-40"
        />

        <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />
    </div>

    <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">

        {/* Hero Heading */}
        <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[115px] mt-[40px]"
        >
            <h1 className="text-[38px] lg:text-[55px] text-[#FFFFFF] max-w-[800px] w-full font-sora font-[600] leading-[1.2] mb-[22px]">
                ServiceNow Services
            </h1>

            <p className="text-[#FFFFFF] font-graphik text-[16px] md:text-[20px] font-[400] leading-[1.6] mb-4">
                Explore Our ServiceNow Solutions & Expertise
            </p>
        </motion.div>

        {/* Hero Description */}
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left md:mt-0"
        >
            <p className="text-[#FFFFFF] font-graphik text-[14px] md:text-[16px] font-[400] leading-[1.6]">
                Explore Mobikode's ServiceNow services designed to support
                your business across consulting, implementation, development,
                managed services, staff augmentation, integrations, and
                dedicated ServiceNow teams. Choose the right ServiceNow
                solution based on your project and business requirements.
            </p>
        </motion.div>

    </div>
</section>

            {/* =========================
                SERVICENOW LINKS
            ========================== */}
            <section className="w-full bg-white py-16 lg:py-24">

                <div className="max-w-[1440px] mx-auto px-5 lg:px-20">

                    {/* Heading */}
                    <div className="max-w-[850px] mb-10 lg:mb-14">

                        <p className="text-[#01386A] font-graphik text-[16px] lg:text-[20px] font-[500] mb-3">
                            ServiceNow Solutions
                        </p>

                        <h2 className="text-[#01386A] font-sora text-[26px] lg:text-[38px] font-[600] leading-[1.3]">
                            Explore Our ServiceNow Services
                        </h2>

                        <p className="text-[#49577A] font-graphik text-[14px] lg:text-[16px] leading-[1.7] mt-5 max-w-[800px]">
                            Explore our specialized ServiceNow services designed
                            to support different business, technical, and
                            platform requirements. Choose a service below to
                            learn more about our ServiceNow expertise.
                        </p>

                    </div>


                    {/* Service Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">

                        {serviceNowPages.map((service, index) => (

                            <Link
                                key={service.path}
                                to={service.path}
                                className="group relative bg-[#F8FAFC] border border-[#E2E8F0] p-7 lg:p-8 min-h-[260px] flex flex-col justify-between overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#01386A] hover:shadow-[0_15px_40px_rgba(1,56,106,0.08)]"
                            >

                                {/* Number */}
                                <div className="absolute top-5 right-6">

                                    <span className="font-sora text-[42px] font-[600] text-[#01386A]/[0.06] group-hover:text-[#ED6800]/[0.10] transition-colors duration-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>

                                </div>


                                {/* Content */}
                                <div className="relative z-10">

                                    <div className="w-[45px] h-[3px] bg-[#ED6800] mb-6 group-hover:w-[65px] transition-all duration-300" />

                                    <h3 className="text-[#01386A] font-sora text-[19px] lg:text-[21px] font-[600] leading-[1.35] pr-8 group-hover:text-[#ED6800] transition-colors duration-300">
                                        {service.title}
                                    </h3>

                                    <p className="text-[#49577A] font-graphik text-[14px] lg:text-[15px] leading-[1.7] mt-4">
                                        {service.description}
                                    </p>

                                </div>


                                {/* Link */}
                                <div className="relative z-10 flex items-center justify-between mt-7">

                                    <span className="text-[#01386A] font-graphik text-[14px] font-[600] group-hover:text-[#ED6800] transition-colors duration-300">
                                        Explore Service
                                    </span>

                                    <div className="w-[38px] h-[38px] rounded-full border border-[#01386A]/20 flex items-center justify-center group-hover:bg-[#ED6800] group-hover:border-[#ED6800] transition-all duration-300">

                                        <ArrowUpRight
                                            size={18}
                                            className="text-[#01386A] group-hover:text-white transition-colors duration-300"
                                        />

                                    </div>

                                </div>

                            </Link>

                        ))}

                    </div>

                </div>

            </section>
            <ServiceNowLeadForm/>
             <Banner
                title="ServiceNow Services"
                text="Explore our ServiceNow consulting, implementation, development, managed services, staff augmentation, and dedicated team solutions."
            />

        </>
    );
};

export default ServiceNowLinks;